import React, { useEffect, useState } from 'react';
import { NblocksProvider, useApp } from '@nebulr-group/nblocks-react';

import { AppRoutes, HandoverRoutes } from './Routes';

import { useLocalStorage, StorageContext } from './hooks/useLocalStorage';
import { APP_ID_KEY, DEFAULT_APP_ID } from './shared/Constants';
import { BrowserRouter } from 'react-router-dom';
import { Config } from './shared/Config';
import { CustomBrandingComponent } from './components/shared/custom-branding-component';
import { LangOverrideParam } from '@nebulr-group/nblocks-react/lib/hooks/theme-context';
import { GTMComponent } from './components/shared/gtm-component';

function App() {
  const debug = !!localStorage.getItem('NB_DEBUG') || process.env.REACT_APP_ENV == 'dev';

  console.log('Cloud Views. Running in env: ' + process.env.REACT_APP_ENV);

  const storage = useLocalStorage('local');
  const [appId, setAppId] = useState<string>(DEFAULT_APP_ID);
  const [customTranslations, setCustomTranslations] = useState<
    LangOverrideParam[]
  >([]);

  useEffect(() => {
    const appId = storage.get(APP_ID_KEY);
    setAppId(appId);

    const setAppIdCallback = (key: string) => {
      if (key !== APP_ID_KEY) {
        return;
      }
      const appId = storage.get(key);
      setAppId(appId);
    };

    storage.on('set', setAppIdCallback);

    return () => {
      storage.off('set', setAppIdCallback);
    };
  }, [storage]);

  const storageResolved = (): boolean => {
    return appId !== DEFAULT_APP_ID;
  };

  useEffect(() => {
    if (storageResolved()) {
      fetchTranslations();
    }
  }, [appId]);

  const renderAppChildren = () => {
    return (
      <BrowserRouter>
        {appId ? (
          <NblocksProvider
            config={{
              debug,
              backendLess: true,
              authLegacy: false,
              oAuthBaseURI: Config.getOauthApiUrl(),
              apiHost: Config.getBackendlessUrl(),
              appId,
            }}
            i18nOverrides={customTranslations}
          >
            <DebuggerWrapper>
              <CustomBrandingComponent url={getCustomCssUrl()} />
              <GTMComponent appId={appId} />
              <AppRoutes />
            </DebuggerWrapper>
          </NblocksProvider>
        ) : (
          <DebuggerWrapper>
            <HandoverRoutes />
          </DebuggerWrapper>
        )}
      </BrowserRouter>
    );
  };

  const getCustomCssUrl = () => {
    return `${Config.getCustomBrandingBaseUrl()}/${appId}/custom.css`;
  };

  const fetchTranslations = () => {
    fetch(`${Config.getCustomBrandingBaseUrl()}/${appId}/translations.json`)
      .then((res) => res.json())
      .catch(() => {
        console.log('No translations');
      })
      .then((json) => {
        if (json) {
          setCustomTranslations([{ lang: 'en', resources: json }]);
        }
      });
  };

  const DebuggerWrapper: React.FunctionComponent<{ children: any }> = ({
    children,
  }) => {
    console.log(`Render for appId ${appId}`);
    const app = useApp();
    if (debug) {
      return (
        <div>
          <div>
            <small>
              {appId} ({app ? app.name : 'loading name...'})
            </small>
            <small>
              (
              {customTranslations.length > 0
                ? JSON.stringify(customTranslations)
                : 'No custom translations'}
              )
            </small>
          </div>
          {children}
        </div>
      );
    } else {
      return <div>{children}</div>;
    }
  };

  return (
    <StorageContext.Provider value={storage as any}>
      {storageResolved() && renderAppChildren()}
    </StorageContext.Provider>
  );
}

export default App;
